/* eslint camelcase: 0 */
import { t } from '@lingui/macro';

import { COLUMN_TYPES, defaultColumnRenderer } from '../../../data-table/DataTableColumns';
import { FORMATTER_TYPES, DEFAULT_INVALID_VALUE, durationFormatter } from '../../../../utilities/FormattingUtilities';
import { buildThresholdColumns } from '../TestLogUtilities';

import { getThresholdsKeys, getThresholdsTooltip } from '../Thresholds';
import { CURL_ERROR_CODES, HTTP_RESPONSE_CODES } from '../TestLogContants';
import { getMetricStatusRendererWithThresholdMetrics, getThresholds } from '../MetricStatus';
import { getWiFiColumns, getWiFiGroupTestColumn } from './WiFiColumns';
import { semColumnRenderer, semColumnTooltip } from '../Sem';
import resultColumnRenderer from '../Result';

function webResultColumnRenderer(row, col) {
    if (row.message?.length > 0) {
        return resultColumnRenderer(row);
    }

    return defaultColumnRenderer(row, col);
}

const metricFormatConfig = {
    type: FORMATTER_TYPES.DURATION,
    fromUnits: 'ms',
    baseUnits: 's',
    forceDecimals: true,
    maxDecimals: 3,
    ltComparison: 0.001,
};

function metricFormatter(value, _row, config) {
    return durationFormatter(value, { ...metricFormatConfig, ...config });
}

function webResponseFormatter(value, row) {
    if (value) {
        const defaultValue = ' ';
        const cleaned = value.toString().replace('-', '');

        if (value >= 0) {
            return t`${cleaned} ${HTTP_RESPONSE_CODES?.[cleaned] ?? defaultValue}`;
        }

        const { message_short } = row;
        if (cleaned === '210') {
            return t`${message_short}`;
        }
        return t`${CURL_ERROR_CODES?.[cleaned] ?? defaultValue}`;
    }

    return DEFAULT_INVALID_VALUE;
}

export function getWebColumns(test) {
    const metricWidth = 72;
    const thresholdMetrics = getThresholdsKeys('Response', test);

    const thresholdColumns = buildThresholdColumns(
        thresholdMetrics.degraded,
        thresholdMetrics.critical,
        t`Total`,
        metricFormatter
    );

    return [
        {
            title: t`Result`,
            width: 120,
            mapping: 'avgResponseCode',
            sortable: true,
            formatter: (value, row) => webResponseFormatter(value, row),
            renderer: webResultColumnRenderer,
        },
        {
            id: 'SEM-message',
            width: 40,
            sortable: false,
            mapping: 'sem-capture-error',
            getTooltip: (row) => semColumnTooltip(row),
            renderer: semColumnRenderer,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Total`,
            width: metricWidth + 5,
            mapping: 'avgResponse',
            sortable: true,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
            getTooltip: (row) => {
                const thresholds = getThresholds(thresholdMetrics, row);
                if (!thresholds) {
                    return '';
                }

                const degraded = metricFormatter(thresholds.degraded);
                const excessive = metricFormatter(thresholds.critical);
                return getThresholdsTooltip(degraded, excessive);
            },
            renderer: getMetricStatusRendererWithThresholdMetrics(test, thresholdMetrics),
        },
        { ...thresholdColumns.degradedColumn, csvFormatter: metricFormatConfig },
        { ...thresholdColumns.excessiveColumn, csvFormatter: metricFormatConfig },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`DNS`,
            width: metricWidth,
            mapping: 'avgDns',
            sortable: true,
            tooltip: false,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Network`,
            width: metricWidth + 30,
            mapping: 'avgNetwork',
            sortable: true,
            tooltip: false,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`SSL`,
            width: metricWidth,
            mapping: 'avgSsl',
            sortable: true,
            tooltip: false,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Client`,
            width: metricWidth + 12,
            mapping: 'avgClient',
            sortable: true,
            tooltip: false,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`App`,
            width: metricWidth,
            mapping: 'avgApp',
            sortable: true,
            tooltip: false,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Server`,
            width: metricWidth + 14,
            mapping: 'avgServer',
            sortable: true,
            tooltip: false,
            formatter: metricFormatter,
            csvFormatter: metricFormatConfig,
        },
        ...getWiFiGroupTestColumn(test),
        ...getWiFiColumns(test),
    ];
}
